import { useMemo } from "react";
import { EventDetailsTableColumns } from "./Data/EventDetailsTableColumns";
import { EventDetailsTableFilters } from "./Data/EventDetailsTableFilters";

/**
 * The Event Details table's columns, column order and filters, from page/org meta
 * where it configures them and from the module's own data files otherwise.
 *
 * Shared by the Digital Twin Performance tab (EventDetailsTable, meta at
 * digitalTwin / IOTDigitalTwin pageMeta metaInfo.table.eventDetailsTable) and the
 * Custom Observability variant (CustomEventDetailsTable, meta at
 * customObservabilityConfig.digitalTwin.table.eventDetailsTable, surfaced as
 * metaInfo.customEventDetailsTable). Both read the same shape:
 *
 *   {
 *     columnsConfig: { <key>: { title, dataIndex, ... } } | [ { title, dataIndex, ... } ],
 *     columnOrdering: [ <key>, ... ],   // optional; defaults to columnsConfig's order
 *     filters: [ { key, type, label, displayType, ... } ]   // optional
 *   }
 *
 * The event list API returns every energyEvent column under its real name and
 * filters/sorts by that name, so a column or filter is added by naming the
 * column - no code change.
 */

/** The date-group column every variant of the table renders first. */
export const EVENT_GROUP_COLUMN = {
  title: "Group",
  dataIndex: "event_group",
  key: "event_group",
  fixed: "left",
  valueRenderingType: "event_group",
};

const EVENT_GROUP_KEY = EVENT_GROUP_COLUMN.key;

/** A columnsConfig array keyed by each column's dataIndex (or key); a map as is. */
const toColumnMap = (columnsConfig) => {
  if (Array.isArray(columnsConfig)) {
    return columnsConfig.reduce((acc, column) => {
      const columnKey = column?.dataIndex || column?.key;
      if (columnKey) acc[columnKey] = column;
      return acc;
    }, {});
  }
  return columnsConfig && typeof columnsConfig === "object" ? { ...columnsConfig } : {};
};

/** "(C)" in a temperature title is shown as "(°C)". */
const withDegreeSymbol = (columns) =>
  Object.fromEntries(
    Object.entries(columns).map(([columnKey, column]) => [
      columnKey,
      typeof column?.title === "string"
        ? { ...column, title: column.title.replace(/\(\s*C\s*\)/g, "(°C)") }
        : column,
    ])
  );

const FALLBACK_COLUMNS = toColumnMap(EventDetailsTableColumns.columnsConfig);
const FALLBACK_ORDERING = EventDetailsTableColumns.columnOrdering ?? [];

/**
 * Resolve the table from a meta block (or nothing).
 *
 * Columns: the configured columnsConfig wins when it names at least one column;
 * the Group column is always present and first, so a config that leaves it out
 * still groups by day. Without a configured ordering the config's own order is
 * used, so a column added to columnsConfig alone is shown rather than dropped.
 *
 * Filters: an array in meta wins (an empty array means "no filters"), otherwise
 * the data file's.
 */
export const resolveEventDetailsTableConfig = (metaTable) => {
  const configuredColumns = toColumnMap(metaTable?.columnsConfig);
  const hasConfiguredColumns = Object.keys(configuredColumns).length > 0;

  let columnsConfig;
  let columnOrdering;
  if (hasConfiguredColumns) {
    const configuredOrdering =
      Array.isArray(metaTable?.columnOrdering) && metaTable.columnOrdering.length > 0
        ? metaTable.columnOrdering
        : Object.keys(configuredColumns);
    columnsConfig = { [EVENT_GROUP_KEY]: EVENT_GROUP_COLUMN, ...configuredColumns };
    columnOrdering = [
      EVENT_GROUP_KEY,
      ...configuredOrdering.filter((columnKey) => columnKey !== EVENT_GROUP_KEY),
    ];
  } else {
    columnsConfig = FALLBACK_COLUMNS;
    columnOrdering = FALLBACK_ORDERING;
  }

  return {
    columnsConfig: withDegreeSymbol(columnsConfig),
    columnOrdering,
    filters: Array.isArray(metaTable?.filters) ? metaTable.filters : EventDetailsTableFilters,
  };
};

/**
 * The resolved table, memoized on the meta block's identity.
 *
 * Pass the block itself (metaInfo.table.eventDetailsTable or
 * metaInfo.customEventDetailsTable), not the whole metaInfo: the result feeds
 * the column and filter effects, and a new object on every render would rebuild
 * the columns and reset the user's column selection.
 */
export const useEventDetailsTableConfig = (metaTable) =>
  useMemo(() => resolveEventDetailsTableConfig(metaTable), [metaTable]);
